/**
 * What the checkout writes back onto the SESSION, so an abandoned payment is legible server-side
 * and not only in the analytics stream.
 *
 * The wizard's own `lastStep`/`stepsVisited` only know about ROUTES, and the whole checkout lives
 * inside a single route — order review, method choice, the card form or the bank coordinates, the
 * confirmation. Without this, every abandoned payment looks identical.
 *
 * Two accumulators, one per rail, because the questions are different. A bank transfer fails by
 * the buyer never making it (so what matters is how far they got and what they copied); a card
 * fails LOUDLY, with a reason the issuer supplied (so what matters is how many times they tried
 * and what Stripe said). They land side by side on `configuration_data`:
 *
 *     configuration_data.payment_method   // the rail the buyer last selected
 *     configuration_data.spei_checkout    // {@link SpeiCheckoutAccounting}
 *     configuration_data.stripe_checkout  // {@link StripeCheckoutAccounting}
 *
 * Every function here is PURE — `(previous, event, now) → next` — and never mutates its input.
 * That is load-bearing twice over: the previous value has already been handed to `sessionSave`,
 * and Redux Toolkit deep-FREEZES what it stores, so mutating it afterwards throws "object is not
 * extensible" and silently loses the accounting. And it makes the merge rules testable without a
 * store, a network, or a rendered component.
 *
 * It lives here, next to the rest of the shared session plumbing (`session-invoice`,
 * `session-payment`, `stripe`), rather than inside one wizard: the `v0-mx` and `v3-mx` flows both
 * write these objects, and the session TYPE has to name them. One definition, one set of tests,
 * no chance of the two flows recording subtly different funnels.
 */
import type { CheckoutProvider } from './session-invoice';

export type { CheckoutProvider };

/* ── SPEI (bank transfer) ─────────────────────────────────────────────────────────────── */

/** One accounting event from the SPEI checkout. All fields optional; merged, never replaced. */
export interface SpeiCheckoutEvent {
  /** Sub-step reached inside the checkout. */
  step?: 'form' | 'instructions' | 'acknowledged';
  /** The payer name the buyer supplied (the name their transfer will come from). */
  fullName?: string;
  /** A coordinate the buyer copied (`clabe`, `reference`, `concept`, `beneficiary`, `bank`,
   *  `amount`) — counted, since copying twice is a real signal about clarity. */
  copied?: string;
}

/** What accumulates on `configuration_data.spei_checkout`. */
export interface SpeiCheckoutAccounting {
  /** The sub-step recorded last, i.e. where the buyer is now. */
  step?: string;
  full_name?: string;
  /**
   * Sub-step → how many times it was reached.
   *
   * Counts, not booleans, because RE-arrival is the interesting signal: a buyer who lands on
   * the bank coordinates three times is going back and forth (correcting the payer name,
   * or returning to re-read a CLABE they failed to copy), which reads very differently from
   * one clean pass. `reached_at` only ever records the first time, so it cannot show this.
   */
  reached?: Record<string, number>;
  /** Sub-step → ISO timestamp of FIRST arrival. */
  reached_at?: Record<string, string>;
  /** Sub-step → ISO timestamp of MOST RECENT arrival (equals `reached_at` on a single pass). */
  last_reached_at?: Record<string, string>;
  /** Coordinate → number of times copied. */
  copied?: Record<string, number>;
}

/** Merge a SPEI event into the accumulator, returning a NEW object. `now` is injected so the
 *  function stays pure (and its timestamps assertable). */
export function applySpeiCheckoutEvent(
  prev: SpeiCheckoutAccounting,
  event: SpeiCheckoutEvent,
  now: string,
): SpeiCheckoutAccounting {
  const next: SpeiCheckoutAccounting = {
    ...prev,
    reached: { ...prev.reached },
    reached_at: { ...prev.reached_at },
    last_reached_at: { ...prev.last_reached_at },
    copied: { ...prev.copied },
  };
  if (event.step) {
    next.step = event.step;
    // Every arrival counts — going Back to the form and returning to the coordinates is a
    // second arrival, and that repetition is the point of the counter.
    (next.reached as Record<string, number>)[event.step] = (prev.reached?.[event.step] ?? 0) + 1;
    // First arrival is written once and never overwritten; the latest always is.
    if (!next.reached_at?.[event.step]) {
      (next.reached_at as Record<string, string>)[event.step] = now;
    }
    (next.last_reached_at as Record<string, string>)[event.step] = now;
  }
  if (event.fullName) next.full_name = event.fullName;
  if (event.copied) {
    (next.copied as Record<string, number>)[event.copied] = (prev.copied?.[event.copied] ?? 0) + 1;
  }
  return next;
}

/* ── Stripe (card) ────────────────────────────────────────────────────────────────────── */

/** Where a card payment ended up. `processing` is a real terminal-ish state for the methods
 *  that settle asynchronously — Stripe accepted it, the money has not landed yet. */
export type StripePaymentStatus = 'processing' | 'succeeded' | 'failed';

/** What Stripe said when an attempt failed. Every field is Stripe's own vocabulary, kept
 *  verbatim so support can look it up rather than decode our paraphrase of it. */
export interface StripeFailure {
  /** `error.code`, e.g. `card_declined`, `incomplete_number`, `expired_card`. */
  code?: string;
  /** `error.decline_code` — the ISSUER's reason, e.g. `insufficient_funds`. The one field that
   *  distinguishes "this card is wrong" from "this card has no money on it". */
  declineCode?: string;
  /** `error.type`, e.g. `card_error` vs `validation_error` vs `api_error`. */
  type?: string;
  /** The message Stripe rendered to the buyer. */
  message?: string;
}

/** One accounting event from the card checkout. All fields optional; merged, never replaced. */
export interface StripeCheckoutEvent {
  /**
   * Sub-step reached inside the card checkout:
   * `form` — the card option was selected, `ready` — Stripe Elements mounted and is chargeable,
   * `submitted` — the buyer pressed Pay, `acknowledged` — the charge cleared and the backend
   * was told.
   */
  step?: 'form' | 'ready' | 'submitted' | 'acknowledged';
  /** The buyer pressed Pay: one attempt. */
  attempt?: boolean;
  /** Stripe validated and collected the card details (`elements.submit()` passed) — i.e. the
   *  buyer really did enter payment information, as opposed to pressing Pay on an empty form. */
  paymentInfo?: boolean;
  /** Where the attempt ended up. */
  status?: StripePaymentStatus;
  /** Stripe's own reason, when it ended up `failed`. */
  error?: StripeFailure;
}

/** One recorded failure, as persisted. */
export interface StripeFailureRecord {
  code?: string;
  decline_code?: string;
  type?: string;
  message?: string;
  at: string;
}

/** What accumulates on `configuration_data.stripe_checkout`. */
export interface StripeCheckoutAccounting {
  /** The sub-step recorded last, i.e. where the buyer is now. */
  step?: string;
  /** Sub-step → how many times it was reached (see the SPEI note: re-arrival is the signal). */
  reached?: Record<string, number>;
  /** Sub-step → ISO timestamp of FIRST arrival. */
  reached_at?: Record<string, string>;
  /** Sub-step → ISO timestamp of MOST RECENT arrival. */
  last_reached_at?: Record<string, string>;
  /** How many times the buyer pressed Pay. Three attempts and no success is a very different
   *  story from one clean charge, and neither shows up in `status` alone. */
  attempts?: number;
  /** Payment details were entered and accepted by Stripe at least once. Distinguishes a buyer
   *  who typed a card and was declined from one who never filled the form in. */
  payment_info_entered?: boolean;
  /** ISO timestamp the details were first entered. */
  payment_info_entered_at?: string;
  /** Where the LAST attempt ended up. */
  status?: StripePaymentStatus;
  /** ISO timestamp of that outcome. */
  status_at?: string;
  /** How many attempts failed. */
  errors?: number;
  /** The most recent failure, in full. */
  last_error?: StripeFailureRecord;
  /** The recent failures, newest first and capped at {@link ERROR_LOG_LIMIT}. A buyer retrying
   *  with three different cards reads differently from one typo corrected — but the log must
   *  not grow without bound inside a session body that is re-POSTed on every save. */
  error_log?: StripeFailureRecord[];
}

/** How many failures `error_log` keeps. Enough to see a pattern, small enough that a buyer
 *  hammering a dead card can't bloat every subsequent `/session/save` body. */
export const ERROR_LOG_LIMIT = 5;

/** Drop the fields Stripe didn't supply, so a record never persists a row of `undefined`s. */
function toFailureRecord(failure: StripeFailure, now: string): StripeFailureRecord {
  const record: StripeFailureRecord = { at: now };
  if (failure.code) record.code = failure.code;
  if (failure.declineCode) record.decline_code = failure.declineCode;
  if (failure.type) record.type = failure.type;
  if (failure.message) record.message = failure.message;
  return record;
}

/** Merge a card event into the accumulator, returning a NEW object. `now` is injected so the
 *  function stays pure (and its timestamps assertable). */
export function applyStripeCheckoutEvent(
  prev: StripeCheckoutAccounting,
  event: StripeCheckoutEvent,
  now: string,
): StripeCheckoutAccounting {
  const next: StripeCheckoutAccounting = {
    ...prev,
    reached: { ...prev.reached },
    reached_at: { ...prev.reached_at },
    last_reached_at: { ...prev.last_reached_at },
    error_log: [...(prev.error_log ?? [])],
  };

  if (event.step) {
    next.step = event.step;
    (next.reached as Record<string, number>)[event.step] = (prev.reached?.[event.step] ?? 0) + 1;
    if (!next.reached_at?.[event.step]) {
      (next.reached_at as Record<string, string>)[event.step] = now;
    }
    (next.last_reached_at as Record<string, string>)[event.step] = now;
  }

  if (event.attempt) next.attempts = (prev.attempts ?? 0) + 1;

  if (event.paymentInfo && !prev.payment_info_entered) {
    next.payment_info_entered = true;
    next.payment_info_entered_at = now;
  }

  if (event.status) {
    next.status = event.status;
    next.status_at = now;
  }

  // A failure is counted whenever Stripe reported one, independently of `status`: a validation
  // error stops the attempt before there is any payment status to report, and it is still the
  // thing that blocked the buyer.
  if (event.error) {
    const record = toFailureRecord(event.error, now);
    next.errors = (prev.errors ?? 0) + 1;
    next.last_error = record;
    next.error_log = [record, ...(prev.error_log ?? [])].slice(0, ERROR_LOG_LIMIT);
  }

  return next;
}
